// import api's and mutations
import { mutations } from 'RestAPIs/rapini';

import type { EditAppointmentType } from 'RestAPIs/activities/types';

// import atoms
import { planUnitsAtom } from '../../atoms';

// import components
import Button from '@sdb/ui/atoms/Button';
import { FormControlLabel, Skeleton, Switch } from '@mui/material';
import {
  DateNavigation,
  LoadingOverlay,
  PlanUnitSelector,
  showSnackbar
} from 'components/shared';
import ActionButton from './ActionButton';
import EntityMenu from './EntityMenu';
import ViewSwitch from './ViewSwitch';
import ZoomLevelMenu from './ZoomLevelMenu';
import type { Period } from 'components/shared/DateNavigation/DateNavigation.types';
import DaypartFilter from '../DaypartFilter/DaypartFilter';

// import enums
import { View } from '../../enums';

// import hooks
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import useCopyMode from '../../hooks/useCopyMode';
import useSelectedDateRange from '../../hooks/useSelectedDateRange';

// import types
import type { HeaderProps } from './Header.types';

// import utils
import { getPlanUnitIds } from '../../../../utils';
import { setUserPreference } from '../../../../../redux/actionCreators';
import {
  StyledHeader,
  StyledHeaderLeftBox,
  StyledHeaderRightBox,
  StyledMainWrapperBox
} from './Header.styles';
import { ZoomLevel } from './ZoomLevelMenu.enums';

export default function Header({
  handleSelectPlanUnit,
  handleChangeView,
  view,
  isEmployeeRole
}: HeaderProps) {
  const { t } = useTranslation('common');
  const {
    hasActivityPlanningEditAccess,
    hasWorkPlacePlanningEditAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasProjectTeamAccess,
    isStandaloneECD
  } = useSelector(({ grants }) => grants);
  const dispatch = useDispatch();
  const selectedPlanunits = useRecoilValue(planUnitsAtom);
  const { activityPlanningOnlyPlanned } = useSelector(
    (state) => state.preferences
  );
  const { startDate, endDate } = useSelectedDateRange();
  const { isCopyMode, endCopyMode } = useCopyMode();

  const saveOnlyPlanned = (value: boolean) => {
    dispatch(
      setUserPreference({
        key: 'activityPlanningOnlyPlanned',
        value
      })
    );
  };

  const { mutate, isLoading } =
    mutations.useSyncAppointmentGroupingsWithDuties();

  const handleSyncClick = useCallback(() => {
    mutate(
      {
        startDate: startDate as string,
        endDate: endDate as string,
        organizationalUnitIds: getPlanUnitIds(selectedPlanunits)
      },
      {
        onSuccess: () => {
          showSnackbar({
            severity: 'success',
            message: t('activityPlanning.syncDutiesSuccess')
          });
        },
        onError: () => {
          showSnackbar({
            severity: 'error',
            message: t('somethingWentWrong')
          });
        }
      }
    );
  }, [startDate, endDate, selectedPlanunits]);

  const { mutate: bulkConnectMutate, isLoading: isBulkConnecting } =
    mutations.useBulkConnectSchedules();

  const handleConnectPlanningClick = useCallback(
    (editAppointmentType: EditAppointmentType) => {
      const planUnitIds = getPlanUnitIds(selectedPlanunits);
      bulkConnectMutate(
        {
          startDate: startDate as string,
          endDate: endDate as string,
          organizationalUnitPlanningEntityPerspective: 'EMPLOYEE',
          planningEntityIdentifiers: planUnitIds.map((id) => ({
            id,
            planningEntityType: 'ORGANIZATIONAL_UNIT' as const
          })),
          editAppointmentType,
          suppressWarnings: false
        },
        {
          onSuccess: () => {
            showSnackbar({
              severity: 'success',
              message: t('activityPlanning.connectPlanningSuccess')
            });
          },
          onError: () => {
            showSnackbar({
              severity: 'error',
              message: t('somethingWentWrong')
            });
          }
        }
      );
    },
    [startDate, endDate, selectedPlanunits, bulkConnectMutate, t]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    saveOnlyPlanned(event.target.checked);
  };

  const getDefaultPeriodByView = (view: View): Period => {
    switch (view) {
      case View.List:
        return 'week';
      case View.Roster:
      case View.RosterPragmatic:
        return 'week';
      case View.Timeline:
      case View.TimelinePragmatic:
        return 'week';
      default:
        return 'day';
    }
  };

  const getPeriodsByView = (view: View): Period[] => {
    switch (view) {
      case View.Timeline:
      case View.TimelinePragmatic:
        return ['day', 'week', 'workweek'];
      case View.Roster:
      case View.RosterPragmatic:
        return ['week', 'workweek'];
      default:
        return ['day', 'week', 'month'];
    }
  };

  return (
    <>
      <StyledHeader component="header">
        <StyledMainWrapperBox>
          <StyledHeaderLeftBox>
            <PlanUnitSelector
              useEmployeePlanunits={isEmployeeRole}
              onSelected={handleSelectPlanUnit}
              fullWidth
              disabled={isCopyMode ? true : undefined}
              hideGroups={isStandaloneECD ? true : (undefined as never)}
            />
            {(view === View.Roster || view === View.RosterPragmatic) && (
              <FormControlLabel
                label={t(`planned`)}
                control={
                  <Switch
                    checked={activityPlanningOnlyPlanned}
                    onChange={handleChange}
                  />
                }
              />
            )}
          </StyledHeaderLeftBox>
          {view ? (
            <DateNavigation
              allowMenuOptions={
                view === View.Timeline ||
                view === View.TimelinePragmatic ||
                view === View.Roster ||
                view === View.RosterPragmatic
              }
              defaultPeriod={getDefaultPeriodByView(view)}
              disabled={isCopyMode}
              options={getPeriodsByView(view)}
            />
          ) : (
            <Skeleton />
          )}
          <StyledHeaderRightBox>
            {(view === View.Cards ||
              view === View.CardsPragmatic ||
              view === View.Timeline ||
              view === View.TimelinePragmatic) && (
              <DaypartFilter isCompact={true} />
            )}
            {(view === View.Roster ||
              view === View.RosterPragmatic ||
              view === View.Cards ||
              view === View.CardsPragmatic ||
              view === View.Timeline ||
              view === View.TimelinePragmatic) && (
              <ZoomLevelMenu
                disabled={isCopyMode}
                disableZoomLevels={
                  view === View.Timeline || view === View.TimelinePragmatic
                    ? [ZoomLevel.DETAILED]
                    : null
                }
                minimized={
                  view === View.Roster ||
                  view === View.RosterPragmatic ||
                  view === View.Timeline ||
                  view === View.TimelinePragmatic
                }
              />
            )}
            {(view === View.Roster ||
              view === View.RosterPragmatic ||
              view === View.List ||
              view === View.Timeline ||
              view === View.TimelinePragmatic) && (
              <EntityMenu view={view} disabled={isCopyMode} />
            )}
            {view && (
              <ViewSwitch
                view={view}
                handleChangeView={handleChangeView}
                disabled={isCopyMode}
              />
            )}
            {(hasActivityPlanningEditAccess ||
              hasWorkPlacePlanningEditAccess ||
              hasMyActivitiesProjectAccess ||
              hasManageOwnProjectsAccess ||
              hasProjectTeamAccess) && (
              <ActionButton
                view={view}
                handleSyncClick={handleSyncClick}
                handleConnectPlanningClick={handleConnectPlanningClick}
                disabled={isCopyMode}
              />
            )}
            {isCopyMode && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => endCopyMode()}
              >
                {t('done')}
              </Button>
            )}
          </StyledHeaderRightBox>
        </StyledMainWrapperBox>
      </StyledHeader>
      <LoadingOverlay loading={isLoading || isBulkConnecting} />
    </>
  );
}
